import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { isSameVatRate, NO_VAT_FILTER_KEY } from '@/lib/utils/vat-rates';
import { useSetColumnSort } from '@/states/column-sort.state';
import { setEditingCellAtom } from '@/states/editing-cell.state';
import { Row } from '@tanstack/react-table';
import { useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';

import { VatRateCell } from '../cells/vat-rate-cell';
import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleVatMargin: (id: string) => void;
  /** pour le surlignage/edit in-place des cellules */
  handleCellClick: (id: string, field: keyof DealItem) => void;

  /** Met à jour en local (et prépare la mutation) un champ K de DealItem */
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;

  /** Termine l’édition (Escape annule l’édition) */
  /** gestion du clavier (Échap / Entrée) */
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  /** gestion du blur (perte de focus) */
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

const isVatMissing = (row: Row<DealItemWithVersions>): boolean => {
  const { activeVersion } = row.original;
  return (
    activeVersion.isSelected &&
    !activeVersion.hasVatMargin &&
    activeVersion.vatRate == null
  );
};

const filterVatRate = (
  row: Row<DealItemWithVersions>,
  _columnId: string,
  filterValue: unknown
): boolean =>
  !Array.isArray(filterValue) ||
  !filterValue.includes(NO_VAT_FILTER_KEY) ||
  isVatMissing(row);

interface VatRateCellContentProps {
  row: Row<DealItemWithVersions>;
  displayValue: number | string | null | undefined;
  isParent: boolean;
  isOfferLocked: boolean;
  isRecentlyModified: boolean;
  handleVatMargin: ColumnProps['handleVatMargin'];
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
}

function VatRateCellContent({
  row,
  displayValue,
  isParent,
  isOfferLocked,
  isRecentlyModified,
  handleVatMargin,
  handleCellClick,
  handleCellEdit,
}: VatRateCellContentProps) {
  const setEditingCell = useSetAtom(setEditingCellAtom);
  const isEditing = useIsCellEditing(row, 'vatRate', false);
  const { activeVersion } = row.original;

  return (
    <VatRateCell
      dealItemId={row.original.id}
      displayValue={displayValue}
      hasVatMargin={activeVersion.hasVatMargin}
      isMarginChecked={
        isParent
          ? row.subRows.every(
              (subRow) => subRow.original.activeVersion.hasVatMargin
            )
          : activeVersion.hasVatMargin
      }
      isParent={isParent}
      isOfferLocked={isOfferLocked}
      isRecentlyModified={isRecentlyModified}
      isEmpty={isParent ? row.subRows.some(isVatMissing) : isVatMissing(row)}
      onToggleVatMargin={handleVatMargin}
      rateSelection={
        isParent ||
        activeVersion.hasVatMargin ||
        activeVersion.updatingFields.includes('productCategory')
          ? undefined
          : {
              isEditing,
              onOpen: () => handleCellClick(activeVersion.id, 'vatRate'),
              onClose: () => setEditingCell(null),
              onSelect: (rate) => {
                if (
                  activeVersion.vatRate == null ||
                  !isSameVatRate(activeVersion.vatRate, rate)
                ) {
                  void handleCellEdit(activeVersion.id, 'vatRate', rate);
                }
                setEditingCell(null);
              },
            }
      }
    />
  );
}

const useColumnDefVatRate = ({
  handleVatMargin,
  handleCellClick,
  handleCellEdit,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'vatRate';

  const getValue = (row: Row<DealItemWithVersions>) => {
    return row.original.activeVersion.vatRate;
  };

  const getParentValue = (row: Row<DealItemWithVersions>) => {
    const subRowsValues: number[] = [];

    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.vatRate;
      if (typeof value !== 'undefined' && value !== null) {
        subRowsValues.push(value);
      }
    });

    if (subRowsValues.length === 0) {
      return null;
    }

    const firstValue = subRowsValues[0];
    const allSame = subRowsValues.every((value) => value === firstValue);

    if (allSame) {
      return firstValue;
    }

    return '≠';
  };

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'VatRate',
      accessorFn: (row) => row.activeVersion?.vatRate ?? 0,
      filterFn: filterVatRate,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="cursor-pointer font-semibold">
          <div
            className="flex flex-1 items-center gap-2"
            onClick={() => setColumnSort(accessorKey)}
          >
            {t('kpi.vat')}
            <SortIcon pointer="vatRate" />
          </div>
        </div>
      ),
      cell: ({ row }) => {
        const isParent = row.subRows.length > 1;
        const displayValue = isParent ? getParentValue(row) : getValue(row);

        const isLoading =
          row.original.activeVersion.updatingFields.includes('vatRate');

        if (isLoading) {
          return (
            <div className="flex w-full items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            </div>
          );
        }

        return (
          <TableCellWithRecentlyModified
            productId={row.original.id}
            field="vatRate"
            isParent={isParent}
          >
            {(isRecentlyModified) => (
              <VatRateCellContent
                row={row}
                displayValue={displayValue}
                isParent={isParent}
                isOfferLocked={isOfferLocked}
                isRecentlyModified={isRecentlyModified}
                handleVatMargin={handleVatMargin}
                handleCellClick={handleCellClick}
                handleCellEdit={handleCellEdit}
              />
            )}
          </TableCellWithRecentlyModified>
        );
      },
      size: 180,
    }),
    [
      t,
      isOfferLocked,
      setColumnSort,
      handleVatMargin,
      handleCellClick,
      handleCellEdit,
    ]
  );
};

export default useColumnDefVatRate;
